'use client'

import { useAppSelector } from '@/redux/hooks'

export const usePermission = () => {
  const { user, isAuthenticated } = useAppSelector((state) => state.auth)
  const userPermissions = user?.permissions || []
  const userRole = user?.role || 'user'

  const hasPermission = (permissionName: string, type: 'read' | 'write' = 'read') => {
    if (userRole === 'super_admin' || userRole === 'admin') return true
    
    // Essential and standard AI creation tools - granting default access to all authenticated users
    if (
      permissionName === 'Generate Avatar' || 
      permissionName === 'View Avatars' || 
      permissionName === 'Generate Video Avatar' || 
      permissionName === 'Delete Avatar' ||
      permissionName === 'View Chat Conversations' ||
      permissionName === 'View Chatbots' ||
      permissionName === 'Generate Article' ||
      permissionName === 'Generate Code' ||
      permissionName === 'Verify Content' ||
      permissionName === 'Generate Text Transcripts' ||
      permissionName === 'View File Conversations' ||
      permissionName === 'Generate Content' ||
      permissionName === 'Manage Rewriting' ||
      permissionName === 'Generate Slide' ||
      permissionName === 'View Plans'
    ) return true
    
    for (const mod of userPermissions) {
      const sub = mod.submodules.find(s => s.name === permissionName)
      if (sub) {
        return type === 'write' ? sub.write : (sub.read || sub.write)
      }
    }
    return false
  }

  const hasAnyPermission = (permissionNames: string[], type: 'read' | 'write' = 'read') => {
    if (userRole === 'super_admin' || userRole === 'admin') return true
    return permissionNames.some(p => hasPermission(p, type))
  }

  const hasAllPermissions = (permissionNames: string[], type: 'read' | 'write' = 'read') => {
    if (userRole === 'super_admin' || userRole === 'admin') return true
    return permissionNames.every(p => hasPermission(p, type))
  }

  const isAdmin = () => userRole === 'admin' || userRole === 'super_admin'
  const isAgent = () => userRole === 'agent'
  const isAssigner = () => userRole === 'assigner'

  return {
    user,
    isAuthenticated,
    permissions: userPermissions,
    role: userRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    isAgent,
    isAssigner
  }
}
